import { HttpBackend, HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { computed, DestroyRef, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ConnectivityService } from '../connectivity';
import { APP_CONFIG } from '../environment';
import { IAppVersionInfo, IWebVersionInfo } from './check-version.model';

@Injectable({
  providedIn: 'root',
})
export class CheckVersionService {
  private readonly config = inject(APP_CONFIG);
  private readonly destroyRef = inject(DestroyRef);
  private readonly connectivity = inject(ConnectivityService);

  private readonly httpBackend = inject(HttpBackend);
  private readonly http: HttpClient;

  private readonly CHECK_INTERVAL_MS = 3600000; // 1 h
  private readonly intervalId?: ReturnType<typeof setInterval>;

  private readonly _serverVersionInfo = signal<IWebVersionInfo | undefined>(undefined);
  private readonly _localAppInfo = signal<IAppVersionInfo | undefined>(undefined);

  public readonly serverVersionInfo = this._serverVersionInfo.asReadonly();
  public readonly localAppInfo = this._localAppInfo.asReadonly();

  public readonly isUpdateRequired = computed<boolean | undefined>(() => {
    const serverInfo = this._serverVersionInfo();
    const localInfo = this._localAppInfo();

    if (!serverInfo?.minVersion || !localInfo?.version) {
      return undefined;
    }

    return this.isVersionOutdated(serverInfo.minVersion, localInfo.version);
  });

  constructor() {
    this.http = new HttpClient(this.httpBackend);

    this.performCheck();

    this.intervalId = setInterval(() => {
      this.performCheck();
    }, this.CHECK_INTERVAL_MS);

    // While offline the check is skipped, so it is forced as soon as the connection is back: from
    // that moment the mandatory update keeps being enforced.
    effect(() => {
      if (this.connectivity.reconnected() > 0) {
        untracked(() => void this.forceCheck());
      }
    });

    this.destroyRef.onDestroy(() => {
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
    });
  }

  public async forceCheck(): Promise<void> {
    await this.performCheck();
  }

  private async performCheck(): Promise<void> {
    if (this.connectivity.isOffline()) {
      // Nothing to check against, and the previous result is kept.
      return;
    }

    try {
      const [localVersion, serverVersion] = await Promise.all([this.fetchLocalVersion(), this.fetchRemoteVersion()]);

      this._localAppInfo.set(localVersion);
      this._serverVersionInfo.set(serverVersion);
    } catch (error) {
      console.error('Error on app version checking:', error);
      this._serverVersionInfo.set(undefined);
    }
  }

  private async fetchLocalVersion(): Promise<IAppVersionInfo> {
    try {
      const webVersion = await firstValueFrom(this.http.get<IWebVersionInfo>(this.config.localVersionsUrl));
      return { version: webVersion.currentVersion, build: webVersion.currentBuild };
    } catch (error) {
      console.error('Error fetching local version:', error);
      return {};
    }
  }

  private async fetchRemoteVersion(): Promise<IWebVersionInfo | undefined> {
    try {
      const httpOptions = {
        headers: new HttpHeaders({
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        }),
        params: new HttpParams().set('cb', Date.now().toString()),
      };

      return await firstValueFrom(this.http.get<IWebVersionInfo>(this.config.versionsUrl, httpOptions));
    } catch (error) {
      console.error('Error fetching remote version:', error);
      return undefined;
    }
  }

  private isVersionOutdated(minVersion: string, currentVersion: string): boolean {
    return this.normalizeVersion(currentVersion) < this.normalizeVersion(minVersion);
  }

  private normalizeVersion(version: string): number {
    return +version
      .split('.')
      .map((v) => v.padStart(4, '0'))
      .join('');
  }
}
